import { mdiAlertCircle, mdiClockStart, mdiCartArrowDown, mdiArrowCollapseHorizontal, mdiClock, mdiFire } from '@mdi/js'
import utils from '@/components/tools/utils'

function queryObjectToQueryString (queryObject) {
  const queryStringElements = []
  for (const [key, value] of Object.entries(queryObject)) {
    queryStringElements.push(key + '=' + value)
  }
  let queryString = queryStringElements.join('&')

  if (queryString !== '') {
    queryString = '?'.concat(queryString)
  }

  return queryString
}

function queryStringToQueryObject (queryString) {
  const query = {}
  queryString = queryString.replace('?', '')
  const vars = queryString.split('&')
  for (let i = 0; i < vars.length; i++) {
    const pair = vars[i].split('=')
    query[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1])
  }
  return query
}

function getPositionCountryCodeLabel (value, that) {
  return that?.$t ? that.$t('catalog.location.country.' + value) : value
}

function getMileageFilterName (value, that) {
  return that?.$t ? that.$t('mileage') + ': ' + splitAndFormatMileageFromTo(value) : value
}

function getFirstRegistrationFilterName (value, that) {
  return that?.$t ? that.$t('first_registration') + ': ' + splitAndFormatFirstRegistrationFromTo(value) : value
}

function getBuyNowFilterName (value, that) {
  return that?.$t ? that.$t('Sofortkauf') : value
}

function getTargetPriceFilterName (value, that) {
  return that?.$t ? that.$t('min_selling_price_anr') + ': ' + splitAndFormatTargetPriceFromTo(value) : value
}

function getAuctionStatusLabel (value, hotbidding, that, isOwner, anrOfferAboveTargetPrice) {
  if (anrOfferAboveTargetPrice) {
    return that?.$t ? that.$t('auction.status.anr_offer_above_targetprice') : 'Vorbehalt'
  }

  switch (value) {
    case 'sold,failed':
      return that?.$t ? that.$t('auction.status.endet') : 'Beendet'
    case 'editing':
      return that?.$t ? that.$t('auction.status.editing') : 'In Bearbeitung'
    case 'under_review':
      return that?.$t ? that.$t('auction.status.under_review') : 'In Prüfung'
    case 'published':
      return that?.$t ? that.$t('auction.status.published') : 'Beginnt in Kürze'
    case 'running':
      if (hotbidding) {
        return that?.$t ? that.$t('auction.status.hotbidding') : 'Hotbid-Phase'
      } else {
        return that?.$t ? that.$t('auction.status.running') : 'Aktiv'
      }

    case 'sold':
      return that?.$t ? that.$t('auction.status.sold') : 'Verkauft'

    case 'renegotiation':
      return that?.$t ? that.$t('auction.status.renegotiation') : 'Nachverhandlung'
    case 'ignored_in_catalog':
      return that?.$t ? that.$t('auction.status.inactive') : 'Inaktiv'

    case 'failed':
      return that?.$t ? that.$t('auction.status.endet') : 'Beendet'
    case 'failed_na':
      return that?.$t ? that.$t('auction.status.failed_na') : 'Beendet'

    case 'acpt_late_offer':
      return that?.$t ? that.$t('auction.status.acpt_late_offer') : 'Verkauft'

    case 'recd_late_offer':
      return that?.$t
        ? (isOwner
            ? that.$t('auction.status.recd_late_offer_owner')
            : that.$t('auction.status.recd_late_offer'))
        : 'Verkauft'

    default:
      return ''
  }
}

function getAuctionStatusColor (value, hotbidding, anrOfferAboveTargetPrice) {
  if (anrOfferAboveTargetPrice) {
    return 'tertiary'
  }

  switch (value) {
    case 'running':
      if (hotbidding) {
        return 'deep-orange'
      } else {
        return 'success'
      }
    case 'failed_na':
      return 'tertiary lighten-3'

    case 'failed':

      return 'error'

    case 'sold':
      return 'success'

    case 'renegotiation':
    case 'under_review':
      return 'orange'
    case 'recd_late_offer':
    case 'acpt_late_offer':
      return 'tertiary'

    default:
      return 'primary'
  }
}

function getAuctionStatusIcon (value, hotbidding) {
  switch (value) {
    case 'editing':
    case 'failed':
    case 'failed_na':
      return mdiAlertCircle

    case 'under_review':
      return mdiClockStart

    case 'published':
      return mdiClockStart

    case 'running':
      if (hotbidding) {
        return mdiFire
      } else {
        return mdiClock
      }

    case 'sold':
      return mdiCartArrowDown

    case 'renegotiation':
    case 'recd_late_offer':
      return mdiArrowCollapseHorizontal

    case 'ignored_in_catalog':
      return mdiAlertCircle

    case 'acpt_late_offer':
      return mdiClock

    default:
      return mdiAlertCircle
  }
}

function getCustomerContextLabel (holdingHighestBid, that) {
  if (holdingHighestBid === true) {
    return that?.$t ? that.$t('customer.context.holding_highest_bid') : 'Sie sind Höchstbietender'
  } else if (holdingHighestBid === false) {
    return that?.$t ? that.$t('customer.context.overbid') : 'Sie wurden überboten'
  }
  return undefined
}

function splitAndFormatMileageFromTo (value) {
  try {
    const splits = value.split(',')
    if (splits[1] === '0') {
      return '> ' + utils.formatNumber(splits[0]) + ' km'
    } else if (splits[0] === '0') {
      return '< ' + utils.formatNumber(splits[1]) + ' km'
    }

    return utils.formatNumber(splits[0]) + ' - ' + utils.formatNumber(splits[1]) + ' km'
  } catch (e) {
    console.error(e)
    return value?.toString()
  }
}

function splitAndFormatFirstRegistrationFromTo (value) {
  try {
    const splits = value.split(',')
    if (splits[1] === '0') {
      return '> ' + splits[0]
    } else if (splits[0] === '0') {
      return '< ' + splits[1]
    }

    return splits[0] + ' - ' + splits[1]
  } catch (e) {
    console.error(e)
    return value?.toString()
  }
}

function splitAndFormatTargetPriceFromTo (value) {
  try {
    const splits = value.split(',')
    if (splits[1] === '0') {
      return `> ${utils.formatCurrency(splits[0])}`
    } else if (splits[0] === '0') {
      return `< ${utils.formatCurrency(splits[1])}`
    }

    return `${utils.formatCurrency(splits[0])} - ${utils.formatCurrency(splits[1])}`
  } catch (e) {
    console.error(e)
    return value?.toString()
  }
}

function getFilterAttributeLabelShort (filterAttributes, attributeCode, value, that) {
  const foundAttribute = filterAttributes[attributeCode]

  if (attributeCode === 'auction_status') {
    return getAuctionStatusLabel(value, false, that)
  }

  if (attributeCode === 'position_country_code') {
    return getPositionCountryCodeLabel(value, that)
  }

  if (attributeCode === 'buy_now') {
    return value === 'true' ? that.$t('buy_now_available') : that.$t('no_buy_now_available')
  }

  if (attributeCode === 'mileage' && value) {
    return splitAndFormatMileageFromTo(value)
  }

  if (attributeCode === 'first_registration' && value) {
    return splitAndFormatFirstRegistrationFromTo(value)
  }

  if (attributeCode === 'target_price' && value) {
    return splitAndFormatTargetPriceFromTo(value)
  }

  const foundOption = foundAttribute?.options.find((o) => {
    if (o.optionId) {
      return o.optionId.toString() === value.toString()
    } else if (o.value) {
      return o.value.toString() === value.toString()
    }

    return false
  })

  if (attributeCode === 'hail_damaged') {
    return that.$t('filter.hail_damaged') + ': ' + foundOption?.label
  }

  if (attributeCode === 'water_damaged') {
    return that.$t('filter.water_damaged') + ': ' + foundOption?.label
  }

  if (attributeCode === 'model_select') {
    return that.$t('filter.model_select') + ': ' + value
  }

  return foundOption?.label
}

function getFilterAttributeLabel (filterAttributes, attributeCode, value, that) {
  const foundAttribute = filterAttributes[attributeCode]

  if (attributeCode === 'auction_status') {
    return foundAttribute?.label + ': ' + getAuctionStatusLabel(value)
  }

  if (attributeCode === 'buy_now') {
    return value === 'true' ? that.$t('buy_now_available') : that.$t('no_buy_now_available')
  }

  if (attributeCode === 'mileage' && value) {
    return foundAttribute?.label + ': ' + splitAndFormatMileageFromTo(value)
  }

  if (attributeCode === 'first_registration' && value) {
    return foundAttribute?.label + ': ' + splitAndFormatFirstRegistrationFromTo(value)
  }

  if (attributeCode === 'target_price' && value) {
    return foundAttribute?.label + ': ' + splitAndFormatTargetPriceFromTo(value)
  }

  if (attributeCode === 'four_wheel_drive') {
    return foundAttribute?.label
  }

  if (attributeCode === 'model_select') {
    return foundAttribute?.label + ': ' + value.replace(',', ', ')
  }

  if (attributeCode === 'position_country_code') {
    return foundAttribute?.label + ': ' + value
  }

  // console.log('getFilterAttributeLabel', attributeCode, value, foundAttribute)
  const foundOptions = foundAttribute?.options.filter((o) => {
    // console.log(o, value, typeof value)
    const values = value?.split(',')
    // console.log(values)

    if (o.optionId) {
      return o.optionId.toString() === value.toString() || values?.includes(o.optionId.toString())
    } else if (o.value) {
      return o.value.toString() === value.toString() || values?.includes(o.value.toString())
    }

    return false
  })

  // concat all labels from foundOptions
  const labels = foundOptions?.map(o => o.label).join(', ')

  if (attributeCode === 'hail_damaged') {
    return that.$t('filter.hail_damaged') + ': ' + labels
  }

  if (attributeCode === 'water_damaged') {
    return that.$t('filter.water_damaged') + ': ' + labels
  }

  return foundAttribute?.label + ': ' + labels
}

const catalog = {
  queryObjectToQueryString,
  queryStringToQueryObject,
  getAuctionStatusLabel,
  getCustomerContextLabel,
  getFilterAttributeLabel,
  getAuctionStatusColor,
  getAuctionStatusIcon,
  getFilterAttributeLabelShort,
  getPositionCountryCodeLabel,
  getMileageFilterName,
  getFirstRegistrationFilterName,
  getBuyNowFilterName,
  getTargetPriceFilterName
}

export default catalog
